import React, { useState, useEffect } from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Badge } from 'primereact/badge'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import { Checkbox } from 'primereact/checkbox'
import { confirmDialog } from 'primereact/confirmdialog'
import { Toast } from 'primereact/toast'

import { _getReportsList, _deleteMultipleReports } from '../../../api/api'
// @ts-ignore - PageHeader is a JSX component
import { PageHeader } from '../../../../shared/PageHeader/PageHeader'
import {OlangItem} from '../../../../shared/Olang/user-interface/OlangItem/OlangItem'

// Type pour les données de rapport du backend
interface BackendReport {
  uid: string
  id?: string | null
  title: string
  srcObject: string
  src?: string | null
  creaDate: string
  filepath?: string
  begDate?: string
  endDate?: string
}

interface RapportExistingListProps {
  onBack: () => void
  onViewReport: (report: BackendReport) => void
  onDeleteReport: (reportId: string) => void
}

const RapportExistingList: React.FC<RapportExistingListProps> = ({
  onBack,
  onViewReport,
  onDeleteReport,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  // État local pour gérer les rapports
  const [reports, setReports] = useState<BackendReport[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedReports, setSelectedReports] = useState<string[]>([])
  const toast = React.useRef<Toast>(null)

  const viewOptions = [
    { label: 'Grille', value: 'grid', icon: 'pi pi-th-large' },
    { label: 'Liste', value: 'list', icon: 'pi pi-list' }
  ]



  // Charger les rapports directement depuis l'API
  const loadReports = async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('📋 Chargement direct des rapports depuis l\'API...')
      const response = await _getReportsList()
      console.log('📋 Réponse API directe:', response)

      // Accéder aux données via response.data (réponse Axios)
      const apiData = response.data
      console.log('📋 Données API:', apiData)

      if (apiData && apiData.success && apiData.result && Array.isArray(apiData.result)) {
        console.log('📋 Rapports chargés avec succès:', apiData.result.length, 'rapports')

        // Trier les rapports par creaDate du plus récent au plus ancien
        const sortedReports = apiData.result.sort((a: BackendReport, b: BackendReport) => {
          const dateA = new Date(a.creaDate).getTime()
          const dateB = new Date(b.creaDate).getTime()
          return dateB - dateA // Tri décroissant (plus récent en premier)
        })

        console.log('📋 Rapports triés par date (plus récent en premier)')
        setReports(sortedReports)
        setError(null)
      } else {
        console.warn('🚫 Réponse API invalide:', apiData)
        setError('Aucun rapport trouvé')
        setReports([])
      }
    } catch (err: any) {
      console.error('🚫 Erreur lors du chargement des rapports:', err)
      setError(err.message || 'Erreur lors du chargement des rapports')
      setReports([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReports()
  }, [])

  // Les rapports sont maintenant gérés directement par l'état local

  const filteredReports = reports.filter(
    (report: BackendReport) =>
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.srcObject?.toLowerCase().includes(searchTerm.toLowerCase())
  )



  const handlePdf = (report: BackendReport) => {
    if (report.filepath) {
      window.open(report.filepath, '_blank')
    }
  }

  const handleSelectReport = (reportId: string) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId)
      } else {
        return [...prev, reportId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedReports.length === filteredReports.length) {
      setSelectedReports([])
    } else {
      setSelectedReports(filteredReports.map(r => r.uid || r.id || ''))
    }
  }

  const handleBulkDelete = () => {
    if (selectedReports.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner au moins un rapport',
        life: 3000
      })
      return
    }

    confirmDialog({
      message: `Voulez-vous vraiment supprimer ${selectedReports.length} rapport(s) ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      accept: async () => {
        try {
          const idsString = selectedReports.join(',')
          console.log('🗑️ Deleting reports with ids:', idsString)

          const response = await _deleteMultipleReports(idsString)
          console.log('🗑️ Delete response:', response)

          if (response.data && !response.data.error) {
            toast.current?.show({
              severity: 'success',
              summary: 'Succès',
              detail: `${selectedReports.length} rapport(s) supprimé(s) avec succès`,
              life: 3000
            })
            setSelectedReports([])
            loadReports() // Reload the list
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de la suppression des rapports',
              life: 3000
            })
          }
        } catch (error: any) {
          console.error('🗑️ Error deleting reports:', error)
          toast.current?.show({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'Erreur lors de la suppression',
            life: 3000
          })
        }
      }
    })
  }

  return (
    <div className='h-full'>
      <Toast ref={toast} />
      {/* Page Header */}
      {/* @ts-ignore */}
      <PageHeader
        title="reports.existing"
        onBack={onBack}
        info={
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-file text-xl text-primary"></i>
              <span className="text-lg font-semibold">{filteredReports.length} rapport{filteredReports.length > 1 ? 's' : ''}</span>
            </div>
            {selectedReports.length > 0 && (
              <>
                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-check-square text-xl text-primary"></i>
                  <span className="text-lg font-semibold">{selectedReports.length} sélectionné{selectedReports.length > 1 ? 's' : ''}</span>
                </div>
              </>
            )}
          </div>
        }
        className="m-3"
      />

      {/* Search Bar with View Mode Toggle and Bulk Actions */}
      <div className='p-3 surface-50 border-bottom-1 surface-border'>
        <div className='flex gap-3 align-items-center mb-2'>
          <div className='p-inputgroup flex-1'>
            <span className='p-inputgroup-addon'>
              <i className='pi pi-search'></i>
            </span>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Rechercher un rapport...'
              className='w-full'
            />
          </div>
          <SelectButton
            value={viewMode}
            onChange={(e) => setViewMode(e.value)}
            options={viewOptions}
            optionLabel='label'
            optionValue='value'
            className='p-selectbutton-sm'
          />
        </div>
        {filteredReports.length > 0 && (
          <div className='flex gap-3 align-items-center'>
            <div className='flex align-items-center gap-2'>
              <Checkbox
                inputId='selectAll'
                checked={selectedReports.length === filteredReports.length && filteredReports.length > 0}
                onChange={handleSelectAll}
              />
              <label htmlFor='selectAll' className='cursor-pointer'>
                Tout sélectionner ({selectedReports.length}/{filteredReports.length})
              </label>
            </div>
            {selectedReports.length > 0 && (
              <Button
                icon='pi pi-trash'
                label={`Supprimer (${selectedReports.length})`}
                className='p-button-danger p-button-sm'
                onClick={handleBulkDelete}
              />
            )}
          </div>
        )}
      </div>

      {/* Reports List */}
      <div className='flex-1 overflow-auto surface-50'>
        {loading ? (
          <div className='flex flex-column align-items-center justify-content-center p-6'>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth='4' animationDuration='1s' />
            <p className='text-600 mt-3'>Chargement des rapports...</p>
            <p className='text-500 text-sm'>Récupération de la liste depuis le serveur</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className='text-center p-6 text-500'>
            <div className='surface-200 border-circle w-4rem h-4rem flex align-items-center justify-content-center mx-auto mb-3'>
              <i className='pi pi-file text-3xl text-400'></i>
            </div>
            <h3 className='text-lg font-medium text-700 mb-2'>
              {reports.length === 0 ? 'Aucun rapport disponible' : 'Aucun rapport trouvé'}
            </h3>
            <p className='text-sm text-500'>
              {reports.length === 0
                ? 'Aucun rapport n\'a encore été généré. Créez votre premier rapport pour commencer.'
                : 'Essayez de modifier votre recherche ou créez un nouveau rapport'
              }
            </p>
          </div>
        ) : (
          <div className='p-3'>
            {viewMode === 'grid' ? (
              // Mode Grille - Cards en colonnes
              <div className='grid'>
                {filteredReports.map((report: BackendReport) => {
                  const reportId = report.uid || report.id || ''
                  const isSelected = selectedReports.includes(reportId)
                  return (
                    <div key={reportId} className='col-12 md:col-6 lg:col-4'>
                      <div className={`surface-card p-4 border-round shadow-2 hover:shadow-4 transition-all transition-duration-300 h-full ${isSelected ? 'border-2 border-primary' : ''}`}>
                        <div className='flex flex-column h-full'>
                          {/* Header de la card */}
                          <div className='flex align-items-center mb-3'>
                            <Checkbox
                              inputId={`report-${reportId}`}
                              checked={isSelected}
                              onChange={() => handleSelectReport(reportId)}
                              className='mr-2'
                            />
                            <div className='bg-primary-100 border-circle p-3 mr-3'>
                              <i className='pi pi-chart-line text-primary text-lg'></i>
                            </div>
                            <div className='flex-1'>
                              <h3 className='text-lg font-semibold text-900 mb-1 line-height-3'>
                                {report.title || 'Rapport sans titre'}
                              </h3>
                              <Badge
                                value='Terminé'
                                severity='success'
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <p className='text-600 mb-3 line-height-3 flex-1'>
                            {report.srcObject === 'sensor_repport' ? 'Rapport de capteurs' : report.srcObject}
                            {report.begDate && report.endDate && (
                              <span className='block text-sm mt-1'>
                                Période: {new Date(report.begDate).toLocaleDateString('fr-FR')} - {new Date(report.endDate).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </p>

                          {/* Date */}
                          <div className='flex align-items-center text-sm text-500 mb-3'>
                            <i className='pi pi-calendar mr-2'></i>
                            <span>
                              {report.creaDate ? new Date(report.creaDate).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              }) : 'Date inconnue'}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className='flex gap-2'>
                            {report.filepath && (
                              <Button
                                icon='pi pi-file-pdf'
                                className='p-button-danger p-button-sm flex-1'
                                tooltip='Télécharger PDF'
                                onClick={() => handlePdf(report)}
                              />
                            )}
                            <Button
                              icon='pi pi-trash'
                              className='p-button-outlined p-button-danger p-button-sm'
                              tooltip='Supprimer'
                              onClick={() => onDeleteReport(report.uid || report.id || '')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              // Mode Liste - Lignes compactes
              <div className='flex flex-column gap-2'>
                {filteredReports.map((report: BackendReport) => {
                  const reportId = report.uid || report.id || ''
                  const isSelected = selectedReports.includes(reportId)
                  return (
                    <div key={reportId} className={`surface-card p-3 border-round shadow-1 hover:shadow-2 transition-all transition-duration-200 ${isSelected ? 'border-2 border-primary' : ''}`}>
                      <div className='flex justify-content-between align-items-center'>
                        <div className='flex align-items-center flex-1'>
                          <Checkbox
                            inputId={`report-list-${reportId}`}
                            checked={isSelected}
                            onChange={() => handleSelectReport(reportId)}
                            className='mr-2'
                          />
                          <div className='bg-primary-100 border-circle p-2 mr-3'>
                            <i className='pi pi-chart-line text-primary text-sm'></i>
                          </div>
                          <div className='flex-1'>
                            <div className='flex align-items-center gap-3 mb-1'>
                              <h3 className='text-base font-semibold text-900 m-0'>
                                {report.title || 'Rapport sans titre'}
                              </h3>
                              <Badge
                                value='Terminé'
                                severity='success'
                                className='p-badge-sm'
                              />
                            </div>
                            <div className='flex align-items-center gap-4 text-sm text-500'>
                              <span className='text-600'>
                                {report.srcObject === 'sensor_repport' ? 'Rapport de capteurs' : report.srcObject}
                                {report.begDate && report.endDate && (
                                  <span className='ml-2'>
                                    ({new Date(report.begDate).toLocaleDateString('fr-FR')} - {new Date(report.endDate).toLocaleDateString('fr-FR')})
                                  </span>
                                )}
                              </span>
                              <div className='flex align-items-center'>
                                <i className='pi pi-calendar mr-1'></i>
                                <span>
                                  {report.creaDate ? new Date(report.creaDate).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  }) : 'Date inconnue'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Actions compactes */}
                        <div className='flex gap-2'>
                          {report.filepath && (
                            <Button
                              icon='pi pi-file-pdf'
                              className='p-button-danger p-button-text'
                              tooltip='PDF'
                              onClick={() => handlePdf(report)}
                              style={{ fontSize: '1.1rem', padding: '0.5rem' }}
                            />
                          )}
                          <Button
                            icon='pi pi-trash'
                            className='p-button-danger p-button-text'
                            tooltip='Supprimer'
                            onClick={() => onDeleteReport(report.uid || report.id || '')}
                            style={{ fontSize: '1.1rem', padding: '0.5rem' }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default RapportExistingList
